// AdminIntakesBuilder.js
import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost, apiPut } from './api';

const emptyIntake = {
  id: null,
  instrument_code: '',
  version: 1,
  discipline_code: 'PROG_PI',
  name: '',
  description: '',
  is_active: 1,
};

const ITEM_TYPES = ['text', 'textarea', 'number', 'date', 'select', 'yesno', 'checkbox', 'likert3', 'likert5'];

function clsx(...xs) { return xs.filter(Boolean).join(' '); }

function Badge({ children, tone = 'slate' }) {
  const map = {
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
    green: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    red: 'bg-rose-100 text-rose-700 border-rose-200',
    amber: 'bg-amber-100 text-amber-800 border-amber-200',
    blue: 'bg-sky-100 text-sky-700 border-sky-200',
    purple: 'bg-purple-100 text-purple-700 border-purple-200',
  };
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-[11px] border', map[tone] || map.slate)}>
      {children}
    </span>
  );
}

function Button({ children, onClick, variant = 'primary', disabled = false, className = '', type = 'button', title }) {
  const base = 'px-4 py-2 rounded-xl text-sm font-semibold transition-colors inline-flex items-center justify-center';
  const styles =
    variant === 'primary'
      ? 'bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-60'
      : variant === 'danger'
      ? 'bg-rose-700 text-white hover:bg-rose-800 disabled:opacity-60'
      : 'bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 disabled:opacity-60';
  return (
    <button title={title} type={type} disabled={disabled} onClick={onClick} className={clsx(base, styles, className)}>
      {children}
    </button>
  );
}

// Small icon-like button for reorder/toggle actions
function MiniBtn({ children, onClick, disabled, title, tone = 'slate' }) {
  const toneMap = {
    slate: 'bg-white border-slate-200 hover:bg-slate-50 text-slate-800',
    red: 'bg-rose-50 border-rose-200 hover:bg-rose-100 text-rose-700',
    green: 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100 text-emerald-700',
    amber: 'bg-amber-50 border-amber-200 hover:bg-amber-100 text-amber-800',
  };
  return (
    <button
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        'px-2 py-1 rounded-lg text-xs font-semibold border inline-flex items-center justify-center disabled:opacity-50',
        toneMap[tone] || toneMap.slate
      )}
      type="button"
    >
      {children}
    </button>
  );
}

function Modal({ open, title, children, onClose, wide = false }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-3">
      {/* Make modal vertically constrained + scrollable body */}
      <div className={clsx('bg-white w-full rounded-2xl shadow-xl border max-h-[90vh] flex flex-col', wide ? 'max-w-5xl' : 'max-w-2xl')}>
        <div className="p-4 border-b flex items-center justify-between gap-3 shrink-0">
          <div className="font-semibold text-slate-900">{title}</div>
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
        <div className="p-4 overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, placeholder = '', type = 'text', mono = false }) {
  return (
    <div>
      <label className="text-xs text-slate-600">{label}</label>
      <input
        type={type}
        className={clsx('w-full px-3 py-2 rounded-xl border bg-white', mono ? 'font-mono text-[13px]' : '')}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function TextAreaField({ label, value, onChange, placeholder = '', rows = 4, mono = false }) {
  return (
    <div>
      <label className="text-xs text-slate-600">{label}</label>
      <textarea
        className={clsx('w-full px-3 py-2 rounded-xl border bg-white', mono ? 'font-mono text-[13px]' : '')}
        value={value}
        placeholder={placeholder}
        rows={rows}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <label className="text-xs text-slate-600">{label}</label>
      <select
        className="w-full px-3 py-2 rounded-xl border bg-white"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={String(o.value)} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function CheckboxField({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 text-sm text-slate-700 mt-2">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

function jsonPretty(v) {
  try {
    if (v === null || v === undefined) return '';
    return JSON.stringify(v, null, 2);
  } catch {
    return '';
  }
}

function parseJsonOrNull(text, { allowEmpty = true } = {}) {
  const raw = (text || '').trim();
  if (!raw) return allowEmpty ? null : null;
  try {
    return JSON.parse(raw);
  } catch {
    return { __invalid_json: true };
  }
}

export default function AdminIntakesBuilder() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyIntake });

  // builder
  const [defOpen, setDefOpen] = useState(false);
  const [defInstrument, setDefInstrument] = useState(null);
  const [defSections, setDefSections] = useState([]);
  const [defItems, setDefItems] = useState([]);

  // add section modal
  const [secModalOpen, setSecModalOpen] = useState(false);
  const [secBusy, setSecBusy] = useState(false);
  const [secForm, setSecForm] = useState({
    code: '',
    title: '',
    sort_order: 10,
    owner_roles: { parent: true, teacher: true, admin: true },
    is_active: true,
  });

  // edit section modal
  const [secEditOpen, setSecEditOpen] = useState(false);
  const [secEditBusy, setSecEditBusy] = useState(false);
  const [secEditTarget, setSecEditTarget] = useState(null);
  const [secEditForm, setSecEditForm] = useState({
    code: '',
    title: '',
    sort_order: 10,
    owner_roles: { parent: true, teacher: true, admin: true },
    is_active: true,
  });

  // add item modal
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [itemBusy, setItemBusy] = useState(false);
  const [itemTargetSection, setItemTargetSection] = useState(null);
  const [itemForm, setItemForm] = useState({
    item_code: '',
    item_type: 'textarea',
    label: '',
    prompt_text: '',
    help_text: '',
    required: false,
    options_json: '',
    scoring_map_json: '',
    sort_order: 10,
    is_active: true,
    external_ref: '',
  });

  // edit item modal
  const [itemEditOpen, setItemEditOpen] = useState(false);
  const [itemEditBusy, setItemEditBusy] = useState(false);
  const [itemEditTarget, setItemEditTarget] = useState(null);
  const [itemEditForm, setItemEditForm] = useState({
    item_code: '',
    item_type: 'textarea',
    label: '',
    prompt_text: '',
    help_text: '',
    required: false,
    options_json: '',
    scoring_map_json: '',
    sort_order: 10,
    is_active: true,
    external_ref: '',
  });

  // quick-action busy map (reorder/toggle)
  const [quickBusy, setQuickBusy] = useState({}); // { [`sec:${id}`]:true, [`it:${id}`]:true }

  function setQuick(key, val) {
    setQuickBusy((p) => ({ ...p, [key]: val }));
  }

  async function loadOrgIntakes() {
    setLoading(true);
    try {
      let data = null;
      try {
        data = await apiGet('/instruments', { scope: 'org', form_kind: 'intake' });
      } catch (e1) {
        data = await apiGet('/instruments', { params: { scope: 'org', form_kind: 'intake' } });
      }
      setItems(data.items || []);
    } catch (e) {
      console.error(e);
      alert('Failed to load org intakes');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadOrgIntakes(); }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (items || []).filter((it) => {
      if (!t) return true;
      return (
        String(it.instrument_code || '').toLowerCase().includes(t) ||
        String(it.name || '').toLowerCase().includes(t) ||
        String(it.discipline_code || '').toLowerCase().includes(t)
      );
    });
  }, [items, q]);

  function openCreate() {
    setForm({ ...emptyIntake });
    setEditOpen(true);
  }

  function openEdit(it) {
    setForm({
      id: it.id,
      instrument_code: it.instrument_code || '',
      version: Number(it.version || 1),
      discipline_code: it.discipline_code || 'PROG_PI',
      name: it.name || '',
      description: it.description || '',
      is_active: it.is_active ? 1 : 0,
    });
    setEditOpen(true);
  }

  async function save() {
    if (!form.instrument_code.trim() || !form.name.trim()) {
      alert('instrument_code and name are required');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        instrument_code: form.instrument_code.trim(),
        version: Number(form.version || 1),
        discipline_code: form.discipline_code.trim() || null,
        name: form.name.trim(),
        description: form.description || null,
        form_kind: 'intake',
        distribution_mode: 'full_content',
        is_active: form.is_active ? 1 : 0,
      };

      if (!form.id) await apiPost('/instruments', payload);
      else await apiPut(`/instruments/${form.id}`, payload);

      setEditOpen(false);
      await loadOrgIntakes();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Save failed');
    } finally {
      setLoading(false);
    }
  }

  async function openDefinition(it) {
    setDefOpen(true);
    try {
      const data = await apiGet(`/instruments/${it.id}/definition`);
      setDefInstrument(data.instrument || null);
      setDefSections(data.sections || []);
      setDefItems(data.items || []);
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to load definition');
      setDefOpen(false);
    }
  }

  async function reloadDefinition() {
    if (!defInstrument?.id) return;
    const data = await apiGet(`/instruments/${defInstrument.id}/definition`);
    setDefSections(data.sections || []);
    setDefItems(data.items || []);
  }

  const sectionCodeById = useMemo(() => {
    const m = {};
    (defSections || []).forEach((s) => { m[s.id] = s.code; });
    return m;
  }, [defSections]);

  // Sorted views for reorder logic
  const sortedSections = useMemo(() => {
    return [...(defSections || [])].sort((a, b) => {
      const ao = Number(a.sort_order || 0);
      const bo = Number(b.sort_order || 0);
      if (ao !== bo) return ao - bo;
      return Number(a.id) - Number(b.id);
    });
  }, [defSections]);

  const itemsBySection = useMemo(() => {
    const map = new Map();
    (defItems || []).forEach((it) => {
      const sid = it.section_id;
      if (!map.has(sid)) map.set(sid, []);
      map.get(sid).push(it);
    });
    // sort each bucket
    for (const [sid, arr] of map.entries()) {
      arr.sort((a, b) => {
        const ao = Number(a.sort_order || 0);
        const bo = Number(b.sort_order || 0);
        if (ao !== bo) return ao - bo;
        return Number(a.id) - Number(b.id);
      });
      map.set(sid, arr);
    }
    return map;
  }, [defItems]);

  // Helpers to build payloads for PUT (so quick actions can update without opening modal)
  function sectionPayloadFromObj(s, overrides = {}) {
    return {
      code: overrides.code != null ? overrides.code : (s.code || ''),
      title: overrides.title != null ? overrides.title : (s.title || ''),
      sort_order: overrides.sort_order != null ? Number(overrides.sort_order) : Number(s.sort_order || 0),
      owner_roles: Array.isArray(s.owner_roles) ? s.owner_roles : [],
      is_active: overrides.is_active != null ? (overrides.is_active ? 1 : 0) : (s.is_active ? 1 : 0),
    };
  }

  function itemPayloadFromObj(it, overrides = {}) {
    return {
      item_code: overrides.item_code != null ? overrides.item_code : (it.item_code || ''),
      item_type: overrides.item_type != null ? overrides.item_type : (it.item_type || 'textarea'),
      label: overrides.label != null ? overrides.label : (it.label || null),
      prompt_text: overrides.prompt_text != null ? overrides.prompt_text : (it.prompt_text || null),
      help_text: overrides.help_text != null ? overrides.help_text : (it.help_text || null),
      required: overrides.required != null ? (overrides.required ? 1 : 0) : (it.required ? 1 : 0),
      options: overrides.options != null ? overrides.options : (it.options != null ? it.options : null),
      scoring_map: overrides.scoring_map != null ? overrides.scoring_map : (it.scoring_map != null ? it.scoring_map : null),
      sort_order: overrides.sort_order != null ? Number(overrides.sort_order) : Number(it.sort_order || 0),
      is_active: overrides.is_active != null ? (overrides.is_active ? 1 : 0) : (it.is_active ? 1 : 0),
      external_ref: overrides.external_ref != null ? overrides.external_ref : (it.external_ref || null),
    };
  }

  // -------------------- QUICK: Reorder + Soft delete Sections --------------------
  async function moveSection(sectionId, dir) {
    const idx = sortedSections.findIndex((s) => Number(s.id) === Number(sectionId));
    if (idx < 0) return;
    const j = dir === 'up' ? idx - 1 : idx + 1;
    if (j < 0 || j >= sortedSections.length) return;

    const a = sortedSections[idx];
    const b = sortedSections[j];

    const keyA = `sec:${a.id}`;
    const keyB = `sec:${b.id}`;
    setQuick(keyA, true); setQuick(keyB, true);

    try {
      const aOrder = Number(a.sort_order || 0);
      const bOrder = Number(b.sort_order || 0);

      // swap sort_order (if equal, nudge to guarantee change)
      const nextA = bOrder;
      const nextB = aOrder === bOrder ? aOrder + (dir === 'up' ? 1 : -1) : aOrder;

      await apiPut(`/instrument-sections/${a.id}`, sectionPayloadFromObj(a, { sort_order: nextA }));
      await apiPut(`/instrument-sections/${b.id}`, sectionPayloadFromObj(b, { sort_order: nextB }));
      await reloadDefinition();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to reorder section');
    } finally {
      setQuick(keyA, false); setQuick(keyB, false);
    }
  }

  async function toggleSectionActive(sectionId) {
    const s = (defSections || []).find((x) => Number(x.id) === Number(sectionId));
    if (!s) return;

    const key = `sec:${s.id}`;
    setQuick(key, true);
    try {
      await apiPut(`/instrument-sections/${s.id}`, sectionPayloadFromObj(s, { is_active: !s.is_active }));
      await reloadDefinition();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to update section');
    } finally {
      setQuick(key, false);
    }
  }

  // -------------------- QUICK: Reorder + Soft delete Items --------------------
  async function moveItem(itemId, dir) {
    const it = (defItems || []).find((x) => Number(x.id) === Number(itemId));
    if (!it) return;

    const arr = itemsBySection.get(it.section_id) || [];
    const idx = arr.findIndex((x) => Number(x.id) === Number(itemId));
    if (idx < 0) return;
    const j = dir === 'up' ? idx - 1 : idx + 1;
    if (j < 0 || j >= arr.length) return;

    const a = arr[idx];
    const b = arr[j];

    const keyA = `it:${a.id}`;
    const keyB = `it:${b.id}`;
    setQuick(keyA, true); setQuick(keyB, true);

    try {
      const aOrder = Number(a.sort_order || 0);
      const bOrder = Number(b.sort_order || 0);

      const nextA = bOrder;
      const nextB = aOrder === bOrder ? aOrder + (dir === 'up' ? 1 : -1) : aOrder;

      await apiPut(`/instrument-items/${a.id}`, itemPayloadFromObj(a, { sort_order: nextA }));
      await apiPut(`/instrument-items/${b.id}`, itemPayloadFromObj(b, { sort_order: nextB }));
      await reloadDefinition();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to reorder item');
    } finally {
      setQuick(keyA, false); setQuick(keyB, false);
    }
  }

  async function toggleItemActive(itemId) {
    const it = (defItems || []).find((x) => Number(x.id) === Number(itemId));
    if (!it) return;

    const key = `it:${it.id}`;
    setQuick(key, true);

    try {
      await apiPut(`/instrument-items/${it.id}`, itemPayloadFromObj(it, { is_active: !it.is_active }));
      await reloadDefinition();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to update item');
    } finally {
      setQuick(key, false);
    }
  }

  // -------------------- Sections: Add --------------------
  function openAddSectionModal() {
    setSecForm({
      code: '',
      title: '',
      sort_order: 10,
      owner_roles: { parent: true, teacher: true, admin: true },
      is_active: true,
    });
    setSecModalOpen(true);
  }

  async function submitAddSection() {
    if (!defInstrument?.id) return;
    const code = (secForm.code || '').trim();
    const title = (secForm.title || '').trim();
    if (!code || !title) {
      alert('Section code and title are required');
      return;
    }
    const roles = Object.entries(secForm.owner_roles).filter(([, v]) => v).map(([k]) => k);
    if (!roles.length) {
      alert('Select at least one owner role');
      return;
    }

    setSecBusy(true);
    try {
      await apiPost(`/instruments/${defInstrument.id}/sections`, {
        code,
        title,
        sort_order: Number(secForm.sort_order || 0),
        owner_roles: roles,
        is_active: secForm.is_active ? 1 : 0,
      });
      setSecModalOpen(false);
      await reloadDefinition();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to add section');
    } finally {
      setSecBusy(false);
    }
  }

  // -------------------- Sections: Edit --------------------
  function openEditSectionModal(section) {
    const rolesArr = Array.isArray(section?.owner_roles) ? section.owner_roles : [];
    const map = {
      parent: rolesArr.includes('parent'),
      teacher: rolesArr.includes('teacher'),
      admin: rolesArr.includes('admin'),
    };

    setSecEditTarget(section);
    setSecEditForm({
      code: section.code || '',
      title: section.title || '',
      sort_order: Number(section.sort_order || 0),
      owner_roles: map,
      is_active: !!section.is_active,
    });
    setSecEditOpen(true);
  }

  async function submitEditSection() {
    if (!secEditTarget?.id) return;

    const code = (secEditForm.code || '').trim();
    const title = (secEditForm.title || '').trim();
    if (!code || !title) {
      alert('Section code and title are required');
      return;
    }

    const roles = Object.entries(secEditForm.owner_roles).filter(([, v]) => v).map(([k]) => k);
    if (!roles.length) {
      alert('Select at least one owner role');
      return;
    }

    setSecEditBusy(true);
    try {
      await apiPut(`/instrument-sections/${secEditTarget.id}`, {
        code,
        title,
        sort_order: Number(secEditForm.sort_order || 0),
        owner_roles: roles,
        is_active: secEditForm.is_active ? 1 : 0,
      });
      setSecEditOpen(false);
      setSecEditTarget(null);
      await reloadDefinition();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to update section');
    } finally {
      setSecEditBusy(false);
    }
  }

  // -------------------- Items: Add --------------------
  function openAddItemModal(section) {
    setItemTargetSection(section);
    setItemForm({
      item_code: '',
      item_type: 'textarea',
      label: '',
      prompt_text: '',
      help_text: '',
      required: false,
      options_json: '',
      scoring_map_json: '',
      sort_order: 10,
      is_active: true,
      external_ref: '',
    });
    setItemModalOpen(true);
  }

  async function submitAddItem() {
    if (!itemTargetSection?.id) return;
    const item_code = (itemForm.item_code || '').trim();
    const item_type = (itemForm.item_type || '').trim();
    if (!item_code || !item_type) {
      alert('Item code and type are required');
      return;
    }
    if (!ITEM_TYPES.includes(item_type)) {
      alert(`Invalid item_type. Allowed: ${ITEM_TYPES.join(', ')}`);
      return;
    }

    const needsOptions = ['select', 'checkbox', 'likert3', 'likert5', 'yesno'].includes(item_type);
    const optionsParsed = parseJsonOrNull(itemForm.options_json, { allowEmpty: true });
    if (needsOptions && optionsParsed?.__invalid_json) {
      alert('Options JSON is invalid');
      return;
    }

    const scoringParsed = parseJsonOrNull(itemForm.scoring_map_json, { allowEmpty: true });
    if (scoringParsed?.__invalid_json) {
      alert('Scoring map JSON is invalid');
      return;
    }

    let options = needsOptions ? (optionsParsed || null) : null;
    if (item_type === 'yesno' && !options) {
      options = [{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }];
    }

    setItemBusy(true);
    try {
      await apiPost(`/instrument-sections/${itemTargetSection.id}/items`, {
        item_code,
        item_type,
        label: (itemForm.label || '').trim() || item_code,
        prompt_text: (itemForm.prompt_text || '').trim() || null,
        help_text: (itemForm.help_text || '').trim() || null,
        required: itemForm.required ? 1 : 0,
        options,
        scoring_map: scoringParsed || null,
        sort_order: Number(itemForm.sort_order || 0),
        is_active: itemForm.is_active ? 1 : 0,
        external_ref: (itemForm.external_ref || '').trim() || null,
      });

      setItemModalOpen(false);
      setItemTargetSection(null);
      await reloadDefinition();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to add item');
    } finally {
      setItemBusy(false);
    }
  }

  // -------------------- Items: Edit --------------------
  function openEditItemModal(item) {
    setItemEditTarget(item);
    setItemEditForm({
      item_code: item.item_code || '',
      item_type: item.item_type || 'textarea',
      label: item.label || '',
      prompt_text: item.prompt_text || '',
      help_text: item.help_text || '',
      required: !!item.required,
      options_json: jsonPretty(item.options),
      scoring_map_json: jsonPretty(item.scoring_map),
      sort_order: Number(item.sort_order || 0),
      is_active: !!item.is_active,
      external_ref: item.external_ref || '',
    });
    setItemEditOpen(true);
  }

  async function submitEditItem() {
    if (!itemEditTarget?.id) return;

    const item_code = (itemEditForm.item_code || '').trim();
    const item_type = (itemEditForm.item_type || '').trim();
    if (!item_code || !item_type) {
      alert('Item code and type are required');
      return;
    }
    if (!ITEM_TYPES.includes(item_type)) {
      alert(`Invalid item_type. Allowed: ${ITEM_TYPES.join(', ')}`);
      return;
    }

    const needsOptions = ['select', 'checkbox', 'likert3', 'likert5', 'yesno'].includes(item_type);

    const optionsParsed = parseJsonOrNull(itemEditForm.options_json, { allowEmpty: true });
    if (needsOptions && optionsParsed?.__invalid_json) {
      alert('Options JSON is invalid');
      return;
    }

    const scoringParsed = parseJsonOrNull(itemEditForm.scoring_map_json, { allowEmpty: true });
    if (scoringParsed?.__invalid_json) {
      alert('Scoring map JSON is invalid');
      return;
    }

    let options = needsOptions ? (optionsParsed || null) : null;
    if (item_type === 'yesno' && !options) {
      options = [{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }];
    }

    setItemEditBusy(true);
    try {
      await apiPut(`/instrument-items/${itemEditTarget.id}`, {
        item_code,
        item_type,
        label: (itemEditForm.label || '').trim() || null,
        prompt_text: (itemEditForm.prompt_text || '').trim() || null,
        help_text: (itemEditForm.help_text || '').trim() || null,
        required: itemEditForm.required ? 1 : 0,
        options,
        scoring_map: scoringParsed || null,
        sort_order: Number(itemEditForm.sort_order || 0),
        is_active: itemEditForm.is_active ? 1 : 0,
        external_ref: (itemEditForm.external_ref || '').trim() || null,
      });

      setItemEditOpen(false);
      setItemEditTarget(null);
      await reloadDefinition();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to update item');
    } finally {
      setItemEditBusy(false);
    }
  }

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {/* Neutral header */}
      <div className="bg-white border rounded-2xl shadow-sm p-4">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xl font-semibold text-slate-900 truncate">
              Org Custom Intakes
            </div>
            <div className="mt-1 text-sm text-slate-600">
              Org admin can create only custom intakes (org-owned). No licensed templates here.
            </div>
          </div>

          <div className="flex flex-wrap gap-2 justify-start lg:justify-end">
            <Button variant="secondary" onClick={loadOrgIntakes} disabled={loading}>
              {loading ? 'Loading…' : 'Reload'}
            </Button>
            <Button variant="primary" onClick={openCreate} disabled={loading}>
              + New Intake
            </Button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-2">
          <div className="lg:col-span-6">
            <label className="text-xs text-slate-600">Search</label>
            <input
              className="w-full px-3 py-2 rounded-xl border bg-white text-slate-900"
              placeholder="Search intakes…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="lg:col-span-6 flex items-end justify-start lg:justify-end">
            <div className="text-xs text-slate-500">
              {loading ? 'Loading…' : `${filtered.length} intakes`}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 bg-white border rounded-2xl shadow-sm overflow-hidden">
        <div className="p-3 border-b flex items-center justify-between">
          <div className="text-sm text-slate-600">{loading ? 'Loading…' : `${filtered.length} intakes`}</div>
          <Button variant="secondary" onClick={loadOrgIntakes} disabled={loading}>
            Refresh
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[1000px] w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr className="text-left">
                <th className="p-3">Code</th>
                <th className="p-3">Name</th>
                <th className="p-3">Discipline</th>
                <th className="p-3">Status</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.id} className="border-b hover:bg-slate-50">
                  <td className="p-3 font-mono text-[12px]">
                    {it.instrument_code} <span className="text-slate-500">v{it.version}</span>
                  </td>
                  <td className="p-3">
                    <div className="font-medium text-slate-900">{it.name}</div>
                    {it.description ? <div className="text-xs text-slate-500 line-clamp-1">{it.description}</div> : null}
                  </td>
                  <td className="p-3">
                    {it.discipline_code ? <Badge>{it.discipline_code}</Badge> : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="p-3">
                    {it.is_active ? <Badge tone="green">active</Badge> : <Badge tone="red">inactive</Badge>}
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex gap-2 justify-end">
                      <Button variant="secondary" onClick={() => openDefinition(it)}>Builder</Button>
                      <Button variant="secondary" onClick={() => openEdit(it)}>Edit</Button>
                    </div>
                  </td>
                </tr>
              ))}
              {!filtered.length && !loading ? (
                <tr><td colSpan={5} className="p-6 text-center text-slate-500">No intakes.</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit modal */}
      <Modal open={editOpen} title={form.id ? 'Edit Intake' : 'Create Intake'} onClose={() => setEditOpen(false)}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <TextField
            label="Intake code"
            value={form.instrument_code}
            onChange={(v) => setForm((p) => ({ ...p, instrument_code: v }))}
            placeholder="MY_INTAKE_CODE"
            mono
          />
          <TextField
            label="Version"
            type="number"
            value={String(form.version)}
            onChange={(v) => setForm((p) => ({ ...p, version: Number(v || 1) }))}
          />
          <div className="md:col-span-2">
            <TextField
              label="Name"
              value={form.name}
              onChange={(v) => setForm((p) => ({ ...p, name: v }))}
              placeholder="Intake name"
            />
          </div>
          <div className="md:col-span-2">
            <TextAreaField
              label="Description"
              value={form.description}
              onChange={(v) => setForm((p) => ({ ...p, description: v }))}
              placeholder="Optional description…"
              rows={4}
            />
          </div>
          <TextField
            label="Discipline"
            value={form.discipline_code}
            onChange={(v) => setForm((p) => ({ ...p, discipline_code: v }))}
            placeholder="PROG_PI"
          />
          <SelectField
            label="Active"
            value={String(form.is_active ? 1 : 0)}
            onChange={(v) => setForm((p) => ({ ...p, is_active: Number(v) }))}
            options={[
              { value: '1', label: 'active' },
              { value: '0', label: 'inactive' },
            ]}
          />
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={loading}>
            Save
          </Button>
        </div>
      </Modal>

      {/* Builder modal */}
      <Modal
        open={defOpen}
        wide
        title={`Builder: ${defInstrument?.instrument_code || ''} v${defInstrument?.version || ''}`}
        onClose={() => setDefOpen(false)}
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div className="lg:col-span-1 border rounded-2xl p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="font-semibold text-slate-900">Sections</div>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={reloadDefinition}>Reload</Button>
                <Button variant="primary" onClick={openAddSectionModal}>+ Section</Button>
              </div>
            </div>

            <div className="mt-3 space-y-2">
              {sortedSections.map((s, idx) => {
                const key = `sec:${s.id}`;
                const busy = !!quickBusy[key] || secEditBusy || secBusy;
                return (
                  <div key={s.id} className="border rounded-xl p-2 hover:bg-slate-50">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-mono text-[12px] text-slate-700">{s.code}</div>
                        <div className="font-medium text-slate-900 truncate">{s.title}</div>
                        <div className="text-xs text-slate-500">
                          roles: {(s.owner_roles || []).join(', ') || '—'}
                        </div>
                        <div className="text-[11px] text-slate-400">order: {Number(s.sort_order || 0)}</div>

                        {/* quick actions row */}
                        <div className="mt-2 flex flex-wrap gap-1">
                          <MiniBtn
                            title="Move up"
                            disabled={busy || idx === 0}
                            onClick={() => moveSection(s.id, 'up')}
                          >
                            ▲
                          </MiniBtn>
                          <MiniBtn
                            title="Move down"
                            disabled={busy || idx === sortedSections.length - 1}
                            onClick={() => moveSection(s.id, 'down')}
                          >
                            ▼
                          </MiniBtn>
                          <MiniBtn
                            title={s.is_active ? 'Deactivate section' : 'Activate section'}
                            tone={s.is_active ? 'red' : 'green'}
                            disabled={busy}
                            onClick={() => toggleSectionActive(s.id)}
                          >
                            {s.is_active ? 'Deactivate' : 'Activate'}
                          </MiniBtn>
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <Badge tone={s.is_active ? 'green' : 'red'}>{s.is_active ? 'active' : 'inactive'}</Badge>
                        <div className="flex gap-1">
                          <Button
                            variant="secondary"
                            className="px-2 py-1 rounded-lg text-xs"
                            onClick={() => openEditSectionModal(s)}
                            disabled={busy}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="secondary"
                            className="px-2 py-1 rounded-lg text-xs"
                            onClick={() => openAddItemModal(s)}
                            disabled={busy}
                          >
                            + Item
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {!defSections.length ? <div className="text-sm text-slate-500">No sections yet.</div> : null}
            </div>
          </div>

          <div className="lg:col-span-2 border rounded-2xl p-3">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-slate-900">Items</div>
              <div className="text-xs text-slate-500">{defItems.length} items</div>
            </div>

            <div className="mt-2 overflow-x-auto">
              <table className="min-w-[1180px] w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="p-2 text-left">Section</th>
                    <th className="p-2 text-left">Code</th>
                    <th className="p-2 text-left">Type</th>
                    <th className="p-2 text-left">Label</th>
                    <th className="p-2 text-left">Required</th>
                    <th className="p-2 text-left">Active</th>
                    <th className="p-2 text-right">Reorder</th>
                    <th className="p-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(defItems || []).map((it) => {
                    const bucket = itemsBySection.get(it.section_id) || [];
                    const idx = bucket.findIndex((x) => Number(x.id) === Number(it.id));
                    const key = `it:${it.id}`;
                    const busy = !!quickBusy[key] || itemEditBusy || itemBusy;

                    return (
                      <tr key={it.id} className="border-b hover:bg-slate-50">
                        <td className="p-2 text-slate-600">
                          {sectionCodeById[it.section_id] || it.section_id}
                        </td>
                        <td className="p-2 font-mono text-[12px]">{it.item_code}</td>
                        <td className="p-2"><Badge>{it.item_type}</Badge></td>
                        <td className="p-2">{it.label || '—'}</td>
                        <td className="p-2">{it.required ? 'Yes' : 'No'}</td>
                        <td className="p-2">{it.is_active ? <Badge tone="green">active</Badge> : <Badge tone="red">inactive</Badge>}</td>

                        <td className="p-2">
                          <div className="flex gap-1 justify-end">
                            <MiniBtn
                              title="Move up within section"
                              disabled={busy || idx <= 0}
                              onClick={() => moveItem(it.id, 'up')}
                            >
                              ▲
                            </MiniBtn>
                            <MiniBtn
                              title="Move down within section"
                              disabled={busy || idx < 0 || idx >= bucket.length - 1}
                              onClick={() => moveItem(it.id, 'down')}
                            >
                              ▼
                            </MiniBtn>
                          </div>
                        </td>

                        <td className="p-2 text-right">
                          <div className="flex gap-1 justify-end">
                            <MiniBtn
                              title={it.is_active ? 'Deactivate item' : 'Activate item'}
                              tone={it.is_active ? 'red' : 'green'}
                              disabled={busy}
                              onClick={() => toggleItemActive(it.id)}
                            >
                              {it.is_active ? 'Deactivate' : 'Activate'}
                            </MiniBtn>
                            <Button
                              variant="secondary"
                              className="px-2 py-1 rounded-lg text-xs"
                              onClick={() => openEditItemModal(it)}
                              disabled={busy}
                            >
                              Edit
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {!defItems.length ? (
                    <tr><td colSpan={8} className="p-4 text-slate-500 text-center">No items.</td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            <div className="mt-3 text-xs text-slate-500">
              Quick actions: reorder ▲▼ and Activate/Deactivate (soft delete) work without opening modals.
            </div>
          </div>
        </div>
      </Modal>

      {/* Add Section modal */}
      <Modal open={secModalOpen} title="Add Section" onClose={() => setSecModalOpen(false)}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <TextField label="Section code" value={secForm.code} onChange={(v) => setSecForm((p) => ({ ...p, code: v }))} placeholder="PARENT" mono />
          <TextField label="Sort order" type="number" value={String(secForm.sort_order)} onChange={(v) => setSecForm((p) => ({ ...p, sort_order: Number(v || 0) }))} />
          <div className="md:col-span-2">
            <TextField label="Title" value={secForm.title} onChange={(v) => setSecForm((p) => ({ ...p, title: v }))} placeholder="Parent information" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-slate-600">Owner roles</label>
            <div className="mt-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
              <CheckboxField
                label="parent"
                checked={!!secForm.owner_roles.parent}
                onChange={(v) => setSecForm((p) => ({ ...p, owner_roles: { ...p.owner_roles, parent: v } }))}
              />
              <CheckboxField
                label="teacher"
                checked={!!secForm.owner_roles.teacher}
                onChange={(v) => setSecForm((p) => ({ ...p, owner_roles: { ...p.owner_roles, teacher: v } }))}
              />
              <CheckboxField
                label="admin"
                checked={!!secForm.owner_roles.admin}
                onChange={(v) => setSecForm((p) => ({ ...p, owner_roles: { ...p.owner_roles, admin: v } }))}
              />
            </div>
          </div>
          <div className="md:col-span-2">
            <CheckboxField
              label="Active"
              checked={!!secForm.is_active}
              onChange={(v) => setSecForm((p) => ({ ...p, is_active: v }))}
            />
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setSecModalOpen(false)} disabled={secBusy}>Cancel</Button>
          <Button variant="primary" onClick={submitAddSection} disabled={secBusy}>
            {secBusy ? 'Saving…' : 'Add Section'}
          </Button>
        </div>
      </Modal>

      {/* Edit Section modal */}
      <Modal
        open={secEditOpen}
        title={`Edit Section${secEditTarget?.code ? `: ${secEditTarget.code}` : ''}`}
        onClose={() => setSecEditOpen(false)}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <TextField
            label="Section code"
            value={secEditForm.code}
            onChange={(v) => setSecEditForm((p) => ({ ...p, code: v }))}
            mono
          />
          <TextField
            label="Sort order"
            type="number"
            value={String(secEditForm.sort_order)}
            onChange={(v) => setSecEditForm((p) => ({ ...p, sort_order: Number(v || 0) }))}
          />
          <div className="md:col-span-2">
            <TextField
              label="Title"
              value={secEditForm.title}
              onChange={(v) => setSecEditForm((p) => ({ ...p, title: v }))}
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-xs text-slate-600">Owner roles</label>
            <div className="mt-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
              <CheckboxField
                label="parent"
                checked={!!secEditForm.owner_roles.parent}
                onChange={(v) => setSecEditForm((p) => ({ ...p, owner_roles: { ...p.owner_roles, parent: v } }))}
              />
              <CheckboxField
                label="teacher"
                checked={!!secEditForm.owner_roles.teacher}
                onChange={(v) => setSecEditForm((p) => ({ ...p, owner_roles: { ...p.owner_roles, teacher: v } }))}
              />
              <CheckboxField
                label="admin"
                checked={!!secEditForm.owner_roles.admin}
                onChange={(v) => setSecEditForm((p) => ({ ...p, owner_roles: { ...p.owner_roles, admin: v } }))}
              />
            </div>
          </div>

          <div className="md:col-span-2">
            <CheckboxField
              label="Active"
              checked={!!secEditForm.is_active}
              onChange={(v) => setSecEditForm((p) => ({ ...p, is_active: v }))}
            />
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setSecEditOpen(false)} disabled={secEditBusy}>Cancel</Button>
          <Button variant="primary" onClick={submitEditSection} disabled={secEditBusy}>
            {secEditBusy ? 'Saving…' : 'Update Section'}
          </Button>
        </div>
      </Modal>

      {/* Add Item modal */}
      <Modal open={itemModalOpen} title={`Add Item${itemTargetSection?.code ? ` to ${itemTargetSection.code}` : ''}`} onClose={() => setItemModalOpen(false)}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <TextField label="Item code" value={itemForm.item_code} onChange={(v) => setItemForm((p) => ({ ...p, item_code: v }))} placeholder="CHILD_NAME" mono />
          <SelectField
            label="Item type"
            value={itemForm.item_type}
            onChange={(v) => setItemForm((p) => ({ ...p, item_type: v }))}
            options={ITEM_TYPES.map((t) => ({ value: t, label: t }))}
          />
          <div className="md:col-span-2">
            <TextField label="Label" value={itemForm.label} onChange={(v) => setItemForm((p) => ({ ...p, label: v }))} placeholder="Question label" />
          </div>
          <div className="md:col-span-2">
            <TextAreaField label="Prompt text" value={itemForm.prompt_text} onChange={(v) => setItemForm((p) => ({ ...p, prompt_text: v }))} placeholder="Optional prompt..." rows={3} />
          </div>
          <div className="md:col-span-2">
            <TextAreaField label="Help text" value={itemForm.help_text} onChange={(v) => setItemForm((p) => ({ ...p, help_text: v }))} placeholder="Optional help..." rows={3} />
          </div>

          <TextField
            label="Sort order"
            type="number"
            value={String(itemForm.sort_order)}
            onChange={(v) => setItemForm((p) => ({ ...p, sort_order: Number(v || 0) }))}
          />

          <div className="md:col-span-1">
            <label className="text-xs text-slate-600">Flags</label>
            <div className="mt-1 space-y-2">
              <CheckboxField
                label="Required"
                checked={!!itemForm.required}
                onChange={(v) => setItemForm((p) => ({ ...p, required: v }))}
              />
              <CheckboxField
                label="Active"
                checked={!!itemForm.is_active}
                onChange={(v) => setItemForm((p) => ({ ...p, is_active: v }))}
              />
            </div>
          </div>

          <div className="md:col-span-2">
            <TextAreaField
              label="Options JSON (for select/checkbox/likert/yesno)"
              value={itemForm.options_json}
              onChange={(v) => setItemForm((p) => ({ ...p, options_json: v }))}
              placeholder='Example: ["A","B","C"] or [{"value":"A","label":"A"}]'
              rows={4}
              mono
            />
            <div className="text-[11px] text-slate-500 mt-1">
              Leave empty for non-choice items. For <span className="font-mono">yesno</span>, empty auto-fills Yes/No.
            </div>
          </div>

          <div className="md:col-span-2">
            <TextAreaField
              label="Scoring map JSON (optional)"
              value={itemForm.scoring_map_json}
              onChange={(v) => setItemForm((p) => ({ ...p, scoring_map_json: v }))}
              placeholder='Example: {"usually":2,"sometimes":1,"rarely":0}'
              rows={4}
              mono
            />
          </div>

          <div className="md:col-span-2">
            <TextField
              label="External ref (optional)"
              value={itemForm.external_ref}
              onChange={(v) => setItemForm((p) => ({ ...p, external_ref: v }))}
              placeholder="Optional external reference"
              mono
            />
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setItemModalOpen(false)} disabled={itemBusy}>Cancel</Button>
          <Button variant="primary" onClick={submitAddItem} disabled={itemBusy}>
            {itemBusy ? 'Saving…' : 'Add Item'}
          </Button>
        </div>
      </Modal>

      {/* Edit Item modal (FIXED: smaller + scroll via Modal) */}
      <Modal
        open={itemEditOpen}
        title={`Edit Item${itemEditTarget?.item_code ? `: ${itemEditTarget.item_code}` : ''}`}
        onClose={() => setItemEditOpen(false)}
        wide={false}
      >
        {/* tighter rows + relies on Modal scroll */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <TextField
            label="Item code"
            value={itemEditForm.item_code}
            onChange={(v) => setItemEditForm((p) => ({ ...p, item_code: v }))}
            mono
          />
          <SelectField
            label="Item type"
            value={itemEditForm.item_type}
            onChange={(v) => setItemEditForm((p) => ({ ...p, item_type: v }))}
            options={ITEM_TYPES.map((t) => ({ value: t, label: t }))}
          />

          <div className="md:col-span-2">
            <TextField
              label="Label"
              value={itemEditForm.label}
              onChange={(v) => setItemEditForm((p) => ({ ...p, label: v }))}
              placeholder="Question label"
            />
          </div>

          <div className="md:col-span-2">
            <TextAreaField
              label="Prompt text"
              value={itemEditForm.prompt_text}
              onChange={(v) => setItemEditForm((p) => ({ ...p, prompt_text: v }))}
              placeholder="Optional prompt..."
              rows={2}
            />
          </div>

          <div className="md:col-span-2">
            <TextAreaField
              label="Help text"
              value={itemEditForm.help_text}
              onChange={(v) => setItemEditForm((p) => ({ ...p, help_text: v }))}
              placeholder="Optional help..."
              rows={2}
            />
          </div>

          <TextField
            label="Sort order"
            type="number"
            value={String(itemEditForm.sort_order)}
            onChange={(v) => setItemEditForm((p) => ({ ...p, sort_order: Number(v || 0) }))}
          />

          <div className="md:col-span-1">
            <label className="text-xs text-slate-600">Flags</label>
            <div className="mt-1 space-y-1">
              <CheckboxField
                label="Required"
                checked={!!itemEditForm.required}
                onChange={(v) => setItemEditForm((p) => ({ ...p, required: v }))}
              />
              <CheckboxField
                label="Active"
                checked={!!itemEditForm.is_active}
                onChange={(v) => setItemEditForm((p) => ({ ...p, is_active: v }))}
              />
            </div>
          </div>

          <div className="md:col-span-2">
            <TextAreaField
              label="Options JSON (for select/checkbox/likert/yesno)"
              value={itemEditForm.options_json}
              onChange={(v) => setItemEditForm((p) => ({ ...p, options_json: v }))}
              rows={3}
              mono
            />
          </div>

          <div className="md:col-span-2">
            <TextAreaField
              label="Scoring map JSON (optional)"
              value={itemEditForm.scoring_map_json}
              onChange={(v) => setItemEditForm((p) => ({ ...p, scoring_map_json: v }))}
              rows={3}
              mono
            />
          </div>

          <div className="md:col-span-2">
            <TextField
              label="External ref (optional)"
              value={itemEditForm.external_ref}
              onChange={(v) => setItemEditForm((p) => ({ ...p, external_ref: v }))}
              placeholder="Optional external reference"
              mono
            />
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setItemEditOpen(false)} disabled={itemEditBusy}>Cancel</Button>
          <Button variant="primary" onClick={submitEditItem} disabled={itemEditBusy}>
            {itemEditBusy ? 'Saving…' : 'Update Item'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}


